import Icon from "../Icon";

/* =========================================================================
   PIEZAS COMPARTIDAS
   Componentes pequeños y sin estado propio. Todo el color sale de las clases
   con tokens: ninguno trae hex sueltos.
   ======================================================================== */

/* -------------------------------- BADGE ------------------------------- */
export function Badge({ tone = "neutral", children, dot = true, title }) {
  return (
    <span
      className={`badge badge-${tone}${dot ? "" : " badge-plain"}`}
      title={title}
    >
      {children}
    </span>
  );
}


/** Badge a partir de un diccionario tipo ORDER_STATES/ROLES. */
export function MapBadge({ map, value, fallback = "—" }) {
  const item = map?.[value];
  if (!item) return <Badge tone="neutral">{fallback}</Badge>;
  return <Badge tone={item.tone}>{item.label}</Badge>;
}


/* ------------------------------ STAT TILE ----------------------------- */

/**
 * Indicador.
 *
 * `tone` tiñe la cifra cuando el valor en sí es una alerta (capacidad cero,
 * cuentas bloqueadas): un número crítico pintado igual que el resto obliga a
 * leer los cuatro para saber cuál importa.
 *
 * `progress` (0..1) dibuja una barra bajo la cifra para las magnitudes que
 * son una fracción de un tope; el número solo no dice si 3 es mucho o poco.
 */
export function StatTile({
  label,
  value,
  icon,
  foot,
  delta,
  spark,
  tone,
  progress,
  /*
   * Si se pasa, la tarjeta se puede pulsar para abrir el desglose de esa cifra.
   *
   * Va como propiedad opcional y no como una tarjeta aparte porque las
   * dieciséis del panel se ven iguales y deben seguir viéndose iguales: lo
   * único que cambia es que algunas responden.
   *
   * Cuando responde deja de ser un `article` y pasa a ser un `button`. No es
   * un detalle de estilo: un `article` con `onClick` no lo alcanza el
   * teclado, no se anuncia como pulsable y no responde a Enter. La alternativa
   * —`role="button"` más `tabIndex` más los manejadores de teclado— es
   * reimplementar a mano lo que el elemento ya trae.
   */
  onClick,
}) {
  const dir =
    delta === undefined || delta === null
      ? null
      : delta > 0.5
        ? "up"
        : delta < -0.5
          ? "down"
          : "flat";

  const Envoltura = onClick ? "button" : "article";

  return (
    <Envoltura
      className={`stat${tone ? ` stat-${tone}` : ""}${onClick ? " stat-pulsable" : ""}`}
      {...(onClick
        ? { type: "button", onClick, title: `Ver el desglose de ${label}` }
        : {})}
    >
      <div className="stat-label">
        {icon && <Icon name={icon} size={13} />}
        {label}
      </div>

      <div className="stat-value">{value}</div>

      {progress !== undefined && progress !== null && (
        <div className="meter stat-meter">
          <span
            style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }}
          />
        </div>
      )}

      {(foot || dir) && (
        <div className="stat-foot">
          {dir && (
            <span className={`delta delta-${dir}`}>
              {dir !== "flat" && (
                <Icon name={dir === "up" ? "arrowUp" : "arrowDown"} size={12} />
              )}
              {Math.abs(delta).toFixed(1).replace(".0", "")}%
            </span>
          )}
          {foot && <span>{foot}</span>}
        </div>
      )}
      {spark}
    </Envoltura>
  );
}


/**
 * Variación contra el periodo anterior.
 *
 * Sin periodo previo NO se inventa un 100 %: se dice que no hay con qué
 * comparar. Un "+100 %" contra cero suena a logro y solo significa que antes
 * no había nada.
 *
 * `sufijo="pp"` para magnitudes que YA son porcentajes. El CTR pasando de
 * 3,1 % a 4,2 % subió un 35 % y también 1,1 puntos: lo primero es cierto y a la
 * vez ilegible, lo segundo es lo que se usa.
 *
 * `menosEsMejor` para tiempos de entrega y demás, donde bajar es la buena
 * noticia. La flecha sigue el dato y el COLOR sigue el significado: un tiempo
 * de entrega que sube pintado de verde es peor que no pintarlo.
 */
export function Variacion({
  actual,
  previo,
  sufijo = "%",
  menosEsMejor = false,
}) {
  const a = Number(actual) || 0;
  const p = Number(previo) || 0;

  if (!p) {
    return (
      <span className="faint">
        {a > 0 ? "Sin periodo anterior con qué comparar" : "Sin actividad"}
      </span>
    );
  }

  const delta = sufijo === "pp" ? a - p : ((a - p) / p) * 100;
  const sube = delta > 0;
  const igual = Math.abs(delta) < 0.05;
  const bueno = menosEsMejor ? !sube : sube;

  return (
    <span
      className="row gap-1"
      style={{
        color: igual ? "var(--text-3)" : bueno ? "var(--ok)" : "var(--bad)",
      }}
    >
      {!igual && <Icon name={sube ? "trendUp" : "trendDown"} size={12} />}
      {igual
        ? "Igual que el periodo anterior"
        : `${sube ? "+" : ""}${delta.toFixed(sufijo === "pp" ? 2 : 1)} ${sufijo} vs. anterior`}
    </span>
  );
}


/* ------------------------------- ESTRELLAS ---------------------------- */
export function Stars({ value = 0, size = 13 }) {
  const n = Math.round(Number(value) || 0);
  return (
    <span className="stars" title={`${Number(value || 0).toFixed(1)} de 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="m12 2.5 2.9 5.9 6.6.9-4.8 4.6 1.2 6.5-5.9-3.1-5.9 3.1 1.2-6.5L2.5 9.3l6.6-.9L12 2.5Z"
            fill={i <= n ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
            opacity={i <= n ? 1 : 0.3}
          />
        </svg>
      ))}
    </span>
  );
}
